import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import { MonetizationStepper } from "@/components/monetization/MonetizationStepper";

interface StructureItem {
  number: number;
  title: string;
  summary: string;
}

const FORMAT_LABELS: Record<string, string> = {
  mini_guide: "Mini Guide",
  full_ebook: "Full Ebook",
  template_pack: "Template Pack",
  mini_course: "Mini Course",
};

export default function MonetizationStructure() {
  const { projectId, productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const format = searchParams.get("format") || "mini_guide";

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [items, setItems] = useState<StructureItem[]>([]);
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (projectId && productId) fetchAndGenerate();
  }, [projectId, productId]);

  const fetchAndGenerate = async () => {
    try {
      const [projectRes, productRes] = await Promise.all([
        supabase.from("monetization_projects").select("*").eq("id", projectId).single(),
        supabase.from("monetization_products").select("*").eq("id", productId).single(),
      ]);
      if (projectRes.error) throw projectRes.error;
      if (productRes.error) throw productRes.error;
      setProject(projectRes.data);
      setProduct(productRes.data);

      // Check if structure already saved
      const existingContent = productRes.data.content as any;
      if (existingContent?.structure?.length > 0 && existingContent?.selectedFormat === format) {
        setItems(existingContent.structure);
        setLoading(false);
        return;
      }

      await generateStructure(productRes.data, projectRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load product");
      setLoading(false);
    }
  };

  const generateStructure = async (productData: any, projectData: any) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "generate-structure",
          params: {
            productName: productData.title,
            format,
            niche: projectData.niche,
            audience: projectData.audience_type,
          },
        },
      });
      if (error) throw error;
      const generated = data?.items || [];
      setItems(generated);

      // Save to product
      await supabase
        .from("monetization_products")
        .update({ content: { ...(productData.content || {}), structure: generated, selectedFormat: format } })
        .eq("id", productId);
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate structure");
    } finally {
      setLoading(false);
    }
  };

  const regenerateItem = async (index: number) => {
    if (!product || !project) return;
    setRegeneratingIndex(index);
    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "generate-structure",
          params: {
            productName: product.title,
            format,
            niche: project.niche,
            audience: project.audience_type,
          },
        },
      });
      if (error) throw error;
      const newItems = data?.items || [];
      if (newItems[index]) {
        const updated = [...items];
        updated[index] = newItems[index];
        setItems(updated);

        await supabase
          .from("monetization_products")
          .update({ content: { ...(product.content || {}), structure: updated, selectedFormat: format } })
          .eq("id", productId);
      }
    } catch {
      toast.error("Failed to regenerate");
    } finally {
      setRegeneratingIndex(null);
    }
  };

  const handleContinue = async () => {
    // Save format selection to product and navigate to build
    await supabase
      .from("monetization_products")
      .update({ content: { ...(product?.content || {}), structure: items, selectedFormat: format } })
      .eq("id", productId);

    navigate(`/app/monetization-ai/project/${projectId}/build/${productId}`);
  };

  const itemLabel = format === "template_pack" ? "Template" : format === "mini_course" ? "Lesson" : "Chapter";

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-green-500" />
        <p className="text-muted-foreground">Generating your product structure...</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/app/monetization-ai/project/${projectId}/format/${productId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Your Product Structure</h1>
            <p className="text-muted-foreground">{FORMAT_LABELS[format]} — {product?.title}</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ml-14">
          <MonetizationStepper currentStep="building" />
        </div>

        {/* Structure Items */}
        <div className="space-y-3 mb-8">
          {items.map((item, i) => (
            <div key={i} className="glass-card p-4 border-green-500/20 flex items-start gap-4">
              <span className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 font-semibold shrink-0 mt-0.5">
                {item.number}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.summary}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => regenerateItem(i)}
                disabled={regeneratingIndex !== null}
              >
                {regeneratingIndex === i ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => generateStructure(product, project)} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate All
          </Button>
          <Button variant="hero" size="lg" onClick={handleContinue}>
            Continue to Content
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
